import * as React from "react";
import { isEmpty, partition } from "lodash";
import { Control, FieldPath, FieldValues } from "react-hook-form";
import {
  CREATOR_AND_UP,
  GUEST,
  includesCreatorAndUpRole,
  includesExternalRole,
  includesInternalRole,
  RoleName,
  RoleNames,
} from "common/authorization";
import { CREATOR_TYPE } from "common/constants";
import { TagSelect } from "../../../../Inputs/react-hook-form";

const isRoleName = (permission: RoleName | string): permission is RoleName => {
  return RoleNames.includes(permission as RoleName);
};

const isCreatorType = (
  permission: CREATOR_TYPE | string,
): permission is CREATOR_TYPE => {
  return Object.values(CREATOR_TYPE).includes(permission as CREATOR_TYPE);
};

export const partitionRolesAndStrings = (
  list: Array<RoleName | string>,
): [Array<RoleName>, Array<string>] => {
  const [roles, strings] = partition(list, permission =>
    isRoleName(permission),
  );

  return [roles, strings];
};

export const generateLabelsForRoles = (roles: Array<RoleName>) => {
  let labels: Array<CREATOR_TYPE> = [];
  if (includesCreatorAndUpRole(roles)) {
    labels.push(CREATOR_TYPE.TEAM);
  }

  if (includesExternalRole(roles)) {
    labels.push(CREATOR_TYPE.GUESTS);
  }
  return labels;
};

const generateRolesFromLabels = (labels: Array<CREATOR_TYPE>) => {
  let values: Array<RoleName | string> = [];
  if (labels.includes(CREATOR_TYPE.GUESTS)) {
    values.push(...GUEST);
  }
  if (labels.includes(CREATOR_TYPE.TEAM)) {
    values.push(...CREATOR_AND_UP);
  }
  return values;
};

const generateRoleOptions = (roles: Array<RoleName>) => {
  let options: Array<{ label: string; value: CREATOR_TYPE }> = [];

  if (includesInternalRole(roles)) {
    options.push({
      label: CREATOR_TYPE.TEAM,
      value: CREATOR_TYPE.TEAM,
    });
  }

  //"Guest" doesn't include publicUser role,
  // for now, public users are selected through a group
  if (roles.includes("guest")) {
    options.push({
      label: CREATOR_TYPE.GUESTS,
      value: CREATOR_TYPE.GUESTS,
    });
  }

  return options;
};

const generateGroupOptions = (userGroups: { name: string; id: string }[]) => {
  return userGroups.map(userGroup => ({
    label: userGroup.name,
    value: userGroup.id,
  }));
};

export const translateLabelsToData = (
  values: Array<CREATOR_TYPE | string>,
): Array<RoleName | string> => {
  if (isEmpty(values)) {
    return [];
  }

  const [roleLabels, userGroups] = partition(values, isCreatorType);

  const roleValues = generateRolesFromLabels(roleLabels);

  return [...roleValues, ...userGroups];
};

export const translateDataToLabels = (
  values: Maybe<Array<RoleName | string>>,
): Array<CREATOR_TYPE | string> => {
  if (!values || !values.length) {
    return [];
  }

  const [roles, groupIds] = partitionRolesAndStrings(values);

  const roleLabels = generateLabelsForRoles(roles);

  return [...roleLabels, ...groupIds];
};

type RoleAndGroupOptions = {
  roles: Array<RoleName>;
  userGroups: { name: string; id: string }[];
};

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues, TName>;
  options: RoleAndGroupOptions;
} & Omit<
  React.ComponentProps<typeof TagSelect>,
  "options" | "isMulti" | "transform" | "control"
>;

export const RoleAndGroupSelector = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  options,
  control,
  iconName = "clipboard-pen",
  ...props
}: Props<TFieldValues, TName>) => {
  return (
    <TagSelect
      options={[
        ...generateRoleOptions(options.roles),
        ...generateGroupOptions(options.userGroups),
      ]}
      isMulti={true}
      iconName={iconName}
      transform={{
        output: values => {
          return translateLabelsToData(values);
        },
        input: values => {
          return translateDataToLabels(values);
        },
      }}
      {...props}
    />
  );
};
